import { Component, OnChanges, ViewChild, Input } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { LegendItem } from "app/shared/data-interaction";
import { Helper } from "app/shared/helper";
import { Chart } from "chart.js";

@Component({
  selector: "app-generic-chart",
  templateUrl: "./generic-chart.component.html",
  styleUrls: ["./generic-chart.component.css"]
})
export class GenericChartComponent implements OnChanges {
  @ViewChild("genericChart") canvas;

  // Inputs
  @Input() chartData; // Data to feed ChartJS
  @Input() chartID; // Id of the chart
  @Input() units; // Desired units for conversion
  @Input() totalsInTooltip; // True to display total values in tooltips
  @Input() chartType; // ChartJS type
  @Input() valueType; // filter value type -> used to convert units into %, absolute or per inhabitant
  @Input() legend; // filter value type -> used to convert units into %, absolute or per inhabitant

  // Local attributes
  private clickable = false; // Allows clicks on legend to change dataset displayed
  private displayLegend = true; // Allows legend to be displayed or not
  private chart: Chart;
  private options; // ChartJS options
  private yAxes; // ChartJS yAxes scale options
  private legendItems: LegendItem[] = []; // Stores info for custom made legend
  private nTicksLeftYAxis: number = 1; // useful to align axes, use the auto value from ChartJS
  private unitTranslateKey: string;

  constructor(
    private helper: Helper,
    private translateService: TranslateService
  ) {}

  ngOnChanges(changes) {
    // create or update chart only when data is provided
    if (changes.chartData && changes.chartData.currentValue) {
      this.updateChartData();
      this.createChart();
      this.updateLegend();
      // initial resize
      this.resizeCallback(this.chart, this.chart.newSize);
      this.chart.update();
    }
  }

  // updates the received chart data with proper translations and units
  // also set the legend clickable if necessary
  updateChartData(): void {
    this.updateChartDataWithTranslation();
    this.updateChartDataForDesiredUnit();
    this.updateLegendDisplay();
  }

  // update displayable and clickable legend if information is provided
  updateLegendDisplay() {
    const displayLegend = this.chartData["display_legend"];
    if (displayLegend != undefined) {
      this.displayLegend = displayLegend;
    }
    else if (this.legend != undefined){
      this.displayLegend = this.legend;
    }
    const clickable = this.chartData["clickable"];
    if (clickable != undefined) {
      this.clickable = clickable;
    }
  }

  // updates the chart labels with the string provided by the translate service
  updateChartDataWithTranslation(): void {
    // Chart labels
    this.chartData.labels.forEach((label, index: number): void => {
      // translate only strings if required
      if (
        this.chartData["translate_chart_labels"] &&
        typeof label == "string"
      ) {
        this.chartData.labels[index] = this.translateService.instant(
          "DATA." + label
        );
      }
    });

    // Datasets label
    this.chartData.datasets.forEach((dataset, index: number): void => {
      const dataLabel = dataset.label;

      // translate only strings
      if (typeof dataLabel == "string") {
        const translatedLabel = this.translateService.instant(
          "DATA." + dataLabel
        );
        this.chartData.datasets[index].label = translatedLabel;
      }
    });
  }

  // updates the datasets data and units to match the unit defined in the config file
  updateChartDataForDesiredUnit(): void {
    // only convert if units are provided from config
    if (this.units) {
      this.chartData.datasets.forEach((dataset, datasetIndex: number): void => {
        // only convert if units are provided in data
        if (dataset["unit"] && dataset["physical_quantity"]) {
          // get the conversion factor
          let currentUnit = dataset["unit"].toLowerCase();
          let currentUnitInfo = this.helper.getUnitInfo(currentUnit);
          let desiredUnit = this.units[dataset["physical_quantity"]];
          let conversionFactor =
            currentUnitInfo.convert_factor_from[desiredUnit];

          // convert each data value and unit
          this.chartData.datasets[datasetIndex]["unit"] = desiredUnit;
          dataset.data.forEach((dataValue, valueIndex: number): void => {
            // replace None with Nan -> used by chartJs spanGaps
            this.chartData.datasets[datasetIndex].data[valueIndex] =
              dataValue != null ? dataValue / conversionFactor : NaN;
          });

          // convert totals if necessary
          if (dataset.totals) {
            // check if totals have different unit
            if (dataset.totals_unit) {
              // get the totals dedicated conversion factor
              currentUnit = dataset["totals_unit"].toLowerCase();
              currentUnitInfo = this.helper.getUnitInfo(currentUnit);
              desiredUnit = this.units[dataset["totals_physical_quantity"]];
              conversionFactor =
                currentUnitInfo.convert_factor_from[desiredUnit];
            }

            // convert totals
            dataset.totals.forEach((total, valueIndex: number): void => {
              this.chartData.datasets[datasetIndex].totals[valueIndex] =
                total / conversionFactor;
            });
          }
        }
      });
    }
  }

  /* Create a custom legend
    Please be aware that legends with the same label will be displayed as a single legend
    item and all dataset will be "clicked".
  */
  updateLegend(): void {
    this.legendItems = [];
    if (this.chart) {
      // legend element is corresponds to each dataset and its color
      this.chart.data.datasets.forEach((dataset, datasetIndex: number) => {
        // check if matching legend already exists
        const matchingLegendIndex = this.legendItems
          .map(legendItem => {
            return legendItem.label;
          })
          .indexOf(dataset.label);
        if (matchingLegendIndex > -1) {
          // update dataset corresponding to existing legend item
          this.legendItems[matchingLegendIndex].datasetIndices.push(
            datasetIndex
          );
        } else {
          // create new legend item
          const legendItem: LegendItem = {
            label: dataset.label,
            color: dataset.backgroundColor
              ? dataset.backgroundColor
              : "#FFFFFF",
            borderColor: dataset.borderColor
              ? dataset.borderColor
              : dataset.backgroundColor,
            borderStyle: dataset.borderColor
              ? dataset.borderDash ? "dashed" : "solid"
              : "none",
            clicked: false,
            datasetIndices: [datasetIndex]
          };

          this.legendItems.push(legendItem);
        }
      });
    }
  }

  createChart(): void {
    const ctx = this.canvas.nativeElement.getContext("2d");
    // translate region name in labels for multi region barcharts
    for (let i = 0; i < this.chartData.labels.length; i++){
      if (this.chartData.labels[i] == "Region") {
        this.chartData.labels[i] = this.translateService.instant(
          `CITIES.${this.chartData.labels[i]}`
        );
      }
    }
    this.resetChart();
    this.setupYAxes();
    this.setupOptions();
    this.options['spanGaps'] = true;
    this.chart = new Chart(ctx, {
      type: this.chartType,
      data: this.chartData,
      options: this.options
    });
  }

  // Setup the axis configuration
  setupYAxes(): void {
    // Setup left axis
    this.yAxes = [
      {
        id: "left",
        position: "left",
        type: "linear",
        scaleLabel: {
          display: true,
          labelString: this.getYAxisLabel("left"),
          fontColor: "black",
          fontSize: 14
        },
        ticks: {
          beginAtZero: true,
          callback: this.yAxisTicksCallback
        },
        stacked: this.chartData["stack_left"],
        afterBuildTicks: (axis, ticks) => {
          // update the number of ticks from the main axis
          this.nTicksLeftYAxis = ticks.length - 1; // don't count start ticks
        }
      }
    ];

    // Setup right axis only if needed
    if (this.isRightYAxisRequired()) {
      this.yAxes.push({
        id: "right",
        position: "right",
        type: "linear",
        scaleLabel: {
          display: true,
          labelString: this.getYAxisLabel("right"),
          fontColor: "black",
          fontSize: 14
        },
        ticks: {
          beginAtZero: true,
          callback: this.yAxisTicksCallback
        },
        stacked: this.chartData["stack_right"],
        gridLines: {
          display: true
        },
        beforeBuildTicks: axis => {
          // configure step size to align the right axis to the left
          axis.chart.options.scales.yAxes[1].ticks.stepSize =
            axis.max / this.nTicksLeftYAxis;
        }
      });
    }
  }

  getYAxisLabel(axisId: string): string {
    const axisLabel: string = this.translateService.instant(
      "DASHBOARD.CHARTS." +
        this.chartID +
        "." +
        axisId.toUpperCase() +
        "_Y_LABEL_NAME"
    );
    let axisUnit: string = "";

    // only add unit if provided
    const providedUnit = this.getYAxisUnit(axisId);
    if (providedUnit != "") {
      axisUnit = " " + providedUnit;
    }

    return axisLabel + axisUnit;
  }

  getYAxisUnit(desiredAxisId: string): string {
    let unitLabel: string = "";

    // search for axis in provided datasets
    // use the unit from first match
    this.chartData["datasets"].every(dataset => {
      const currentYAxisID = dataset["yAxisID"];

      // check if axis is specified
      if (currentYAxisID) {
        if (currentYAxisID == desiredAxisId) {
          const datasetUnit = dataset["unit"];
          // check if unit is provided
          if (datasetUnit) {
            if (this.chartData['force_unit']) {
              this.unitTranslateKey = datasetUnit;
            }
            else {
              this.unitTranslateKey = this.helper.getUnitIdFromFilterValue(
                this.chartData.value_type ? this.valueType : "absolute",
                datasetUnit
              ).translate_unit;
            }
            unitLabel = this.translateService.instant(
              "UNITS." + this.unitTranslateKey
            );

            // same as break
            return false;
          }
        }
      }
      // same as continue
      return true;
    });

    return unitLabel;
  }

  yAxisTicksCallback = value => {
    // add thousand separator where needed, and round values larger than 10
    return this.helper.getThousandSeparatorFromNumber(value);
  };

  // Returns true if right axis need to be setup
  isRightYAxisRequired(): boolean {
    let required = false;

    // search in each dataset to check if right axis is present
    this.chartData["datasets"].every(dataset => {
      const yAxisID = dataset["yAxisID"];

      // check if axis is specified
      if (yAxisID) {
        if (yAxisID == "right") {
          required = true;
        }
      }

      // force exit "every loop" when right axis is found
      return !required;
    });

    return required;
  }

  // Setup the chart options
  setupOptions(): void {
    this.options = {
      responsive: true,
      maintainAspectRatio: false,
      legend: { display: false },
      scales: { 
        yAxes: this.yAxes, 
        xAxes: [
          {
            offset: true,
            gridLines: {display: false,}
          }
        ]
      },
      tooltips: {
        callbacks: {
          label: this.tooltipsLabelCallback,
          footer: this.tooltipsFooterCallback
        }
      },
      onResize: this.resizeCallback
    };
    if (this.chartData['datasets'][0]['totals']){
      let positivCount = 0
      this.chartData['datasets'][0]['totals'].forEach(num =>{
        if (num >= 0){
          positivCount += 1
        }
      })
      if (positivCount == 0){
        this.options.scales.xAxes[0]['position'] = 'top';
        this.options.scales.xAxes[0]['gridLines']['display'] = true;
      }
    }
  }

  resizeCallback = (chartInstance, newSize) => {
    // resize scatter chart line to the width of the stack
    const scatterLineDatasets = chartInstance.data.datasets.filter(
      dataset => dataset.type == "scatter" && dataset.pointStyle == "line"
    );

    scatterLineDatasets.forEach(dataset => {
      // bar width from stackoverflow : https://stackoverflow.com/questions/45707618/chart-js-get-bar-width-after-render
      const barWidth = chartInstance.getDatasetMeta(1).data[0]._model.width / 2;
      // display slightly larger line
      dataset.pointRadius = barWidth * 1.3;
      dataset.pointHoverRadius = barWidth * 1.4;
    });
  };

  tooltipsLabelCallback = (tooltipItem, data) => {
    const datasetIndex = tooltipItem.datasetIndex;
    const dataLabel = data.datasets[datasetIndex].label + " :";
    let dataValue = tooltipItem.value;
    let dataUnit = ""; // no unit by default

    // translate unit only if any
    const datasetUnit = this.chartData.datasets[datasetIndex]["unit"];
    if (datasetUnit) {
      // adapt unit according to value type if needed
      if (this.chartData['force_unit']) {
        this.unitTranslateKey = datasetUnit;
      }
      else {
        this.unitTranslateKey = this.helper.getUnitIdFromFilterValue(
          this.chartData.value_type ? this.valueType : "absolute",
          datasetUnit
        ).translate_unit;
      }
      const convertedToSmaller = this.helper.convertTooBigValue(
        dataValue,
        this.unitTranslateKey
      );

      dataValue = convertedToSmaller[0];
      dataUnit =
        " " + this.translateService.instant("UNITS." + convertedToSmaller[1]);
    }

    // apply same format as axes
    dataValue = this.helper.getThousandSeparatorFromNumber(dataValue);

    return [dataLabel, dataValue + dataUnit];
  };

  tooltipsFooterCallback = (tooltipItem, data) => {
    const datasetIndex = tooltipItem[0].datasetIndex;
    const dataIndex = tooltipItem[0].index;
    let retval = [];

    // display only if tooltips is requested
    if (this.totalsInTooltip) {
      const totals = this.chartData.datasets[datasetIndex]["totals"];

      // display only if totals provided
      if (totals) {
        let totalValue = totals[dataIndex];
        // use total unit if provided or dataset unit by default
        const datasetUnit = this.chartData.datasets[datasetIndex]["totals_unit"]
          ? this.chartData.datasets[datasetIndex]["totals_unit"]
          : this.chartData.datasets[datasetIndex]["unit"];
        const totalTranslation = this.translateService.instant(
          `DASHBOARD.CHARTS.${this.chartID}.TOTAL_TOOLTIP`
        );

        let totalUnit = ""; // no unit by default

        if (datasetUnit) {
          // adapt unit according to value type if needed
          const unitTranslateKey = this.helper.getUnitIdFromFilterValue(
            this.chartData.value_type ? this.valueType : "absolute",
            datasetUnit
          ).translate_unit;
          const convertedToSmaller = this.helper.convertTooBigValue(
            totalValue,
            unitTranslateKey
          );

          totalValue = convertedToSmaller[0];
          totalUnit =
            " " +
            this.translateService.instant("UNITS." + convertedToSmaller[1]);
        }

        // apply same format as axes
        totalValue = this.helper.getThousandSeparatorFromNumber(totalValue);

        return [totalTranslation, totalValue + totalUnit];
      }
    }

    return retval;
  };

  // Legend click callback
  onLegendClick(legendIndex: number) {
    // invert each dataset linked to the clicked legend item
    this.legendItems[legendIndex].datasetIndices.forEach(datasetIndex => {
      let clickedDataset = this.chart.data.datasets[datasetIndex];
      clickedDataset.hidden = !clickedDataset.hidden;
    });
    this.chart.update();
  }

  resetChart(): void {
    if (this.chart) {
      this.chart.destroy();
      this.chart = void 0;
    }
  }
}
